/**
 * 시간대별 추이 그래프의 계산 (22-B회차, 빠진 정보 1·5).
 *
 * 통계의 요청 수·상위 5%·오류율과 실시간의 초당 요청·오류가 같은 그림을 쓴다. 여기 있는 것은
 * 전부 순수 함수다. 그리는 쪽(`TrendChart`)은 결과를 캔버스에 옮기기만 한다.
 *
 * 시간 구간(bucket)은 `floor(t / bucketMs) * bucketMs`로 자른다. 서버의 시간대별 집계와
 * 같은 식이라 서버가 준 점이 그대로 자리에 놓인다. 자료가 없는 구간은 서버가 주지 않으므로
 * 여기서 빈 자리(`undefined`)로 채운다 — 0으로 채우면 "요청이 0건"과 "자료가 없음"이 섞인다.
 */

export interface TrendSeries {
  label: string;
  /** `#rrggbb`. 캔버스는 CSS 클래스를 못 쓴다 */
  color: string;
  /** 값의 종류. 눈금과 말풍선 표기가 다르다 */
  unit: 'count' | 'ms' | 'pct' | 'rate';
  /** 막대(건수)인지 선(시간·비율)인지 */
  shape: 'bar' | 'line';
  /** 구간마다 값 하나. 자료가 없는 구간은 undefined */
  values: readonly (number | undefined)[];
}

/** 기간을 구간 시작 시각으로 나눈다. 양 끝은 포함이다 */
export function bucketStarts(from: number, to: number, bucketMs: number): number[] {
  if (bucketMs <= 0 || to < from) return [];
  const first = Math.floor(from / bucketMs) * bucketMs;
  const last = Math.floor(to / bucketMs) * bucketMs;
  const out: number[] = [];
  for (let at = first; at <= last; at += bucketMs) out.push(at);
  return out;
}

/** 서버가 준 점을 구간 자리에 맞춘다. 없는 구간은 undefined */
export function alignToBuckets<T extends { bucketStart: number }>(
  points: readonly T[],
  buckets: readonly number[],
  pick: (point: T) => number | undefined,
): (number | undefined)[] {
  const byStart = new Map<number, T>();
  for (const point of points) byStart.set(point.bucketStart, point);
  return buckets.map((start) => {
    const point = byStart.get(start);
    return point === undefined ? undefined : pick(point);
  });
}

/**
 * 세로축 상한. 관측 최댓값을 1·2·5 × 10ⁿ으로 올려 잡는다.
 * 0이나 값이 없으면 1이다. 상한이 값과 같으면 선이 천장에 붙어 읽기 어렵다
 */
export function niceMax(values: readonly (number | undefined)[]): number {
  let max = 0;
  for (const value of values) if (value !== undefined && value > max) max = value;
  if (!Number.isFinite(max) || max <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 5, 10]) {
    if (max <= step * power) return step * power;
  }
  return 10 * power;
}

/** 비율(0~1)은 100%를 넘지 않는다. 오류율은 상한을 값에 따라 늘리되 1을 넘기지 않는다 */
export function niceMaxRate(values: readonly (number | undefined)[]): number {
  return Math.min(1, niceMax(values));
}

/** 값 표기. 단위마다 다르다 */
export function formatValue(value: number, unit: TrendSeries['unit']): string {
  switch (unit) {
    case 'count':
      return `${Math.round(value).toLocaleString('ko-KR')}건`;
    case 'ms':
      return value >= 1_000 ? `${(value / 1_000).toFixed(1)}초` : `${Math.round(value)}ms`;
    case 'pct':
      return `${(value * 100).toFixed(1)}%`;
    default:
      return `${value.toFixed(1)}건/초`;
  }
}

/** 세로 눈금 표기. 값 표기보다 짧게 */
export function formatAxis(value: number, unit: TrendSeries['unit']): string {
  switch (unit) {
    case 'count':
      return Math.round(value).toLocaleString('ko-KR');
    case 'ms':
      return value >= 1_000 ? `${value / 1_000}s` : `${Math.round(value)}ms`;
    case 'pct':
      return `${Math.round(value * 100)}%`;
    default:
      return value.toFixed(value >= 10 ? 0 : 1);
  }
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** 가로 눈금 시각. 하루를 넘는 기간이면 날짜를 붙인다 */
export function formatTimeTick(at: number, spanMs: number): string {
  const date = new Date(at);
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return spanMs > 24 * 3_600_000 ? `${date.getMonth() + 1}/${date.getDate()} ${clock}` : clock;
}

/** 가로 눈금을 찍을 구간 번호. 많아야 `maxTicks`개, 고르게 */
export function tickIndexes(count: number, maxTicks = 6): number[] {
  if (count <= 0) return [];
  if (count <= maxTicks) return Array.from({ length: count }, (_, index) => index);
  const step = Math.ceil(count / maxTicks);
  const out: number[] = [];
  for (let index = 0; index < count; index += step) out.push(index);
  return out;
}

/** 구간 번호 → 구간의 왼쪽 x. 폭은 `width / count` */
export function xOfIndex(index: number, count: number, width: number): number {
  return count === 0 ? 0 : (index / count) * width;
}

/** x → 구간 번호. 밖이면 undefined */
export function indexAtX(x: number, count: number, width: number): number | undefined {
  if (count === 0 || width <= 0 || x < 0 || x >= width) return undefined;
  return Math.min(count - 1, Math.floor((x / width) * count));
}

/**
 * 그림에서 누른 자리 → 구간 번호와 계열 번호 (23-A회차, UI 피드백 17).
 * 계열은 위에서부터 0번이다. 계열 사이 간격에 떨어져도 가장 가까운 계열로 본다.
 * 세로 눈금 줄(맨 아래)이나 그림 밖이면 undefined
 */
export function pickAt(
  x: number,
  y: number,
  count: number,
  width: number,
  layout: { rowHeight: number; rowGap: number; seriesCount: number },
): { index: number; series: number } | undefined {
  const index = indexAtX(x, count, width);
  if (index === undefined || y < 0) return undefined;
  const pitch = layout.rowHeight + layout.rowGap;
  const series = Math.floor(y / pitch);
  if (series < 0 || series >= layout.seriesCount) return undefined;
  return { index, series };
}

/** 값 → y. 위가 크다 */
export function yOfValue(value: number, max: number, height: number): number {
  if (max <= 0) return height;
  return height - Math.min(1, Math.max(0, value / max)) * height;
}

/**
 * 말풍선 한 줄. "10:05 · 요청 12건 · 상위 5% 80ms". 자료가 없는 구간은 "자료 없음"
 */
export function describeBucket(
  at: number,
  spanMs: number,
  series: readonly TrendSeries[],
  index: number,
): string {
  const parts = series.map((item) => {
    const value = item.values[index];
    return value === undefined
      ? `${item.label} 자료 없음`
      : `${item.label} ${formatValue(value, item.unit)}`;
  });
  return [formatTimeTick(at, spanMs), ...parts].join(' · ');
}
